import { BinaryFilter, Filter } from '@cubejs-client/core';

type OptionNode = {
  name: string;
  level: number;
  parent?: string;
  children: string[];
  selected: boolean;
  childrenCount: number;
  selectedChildrenCount: number;
  hasSelectedSiblings: boolean;
  isRoot: boolean;
  parentSelected: boolean;
  isOnlyChild: boolean;
  allDescendantsSelected: boolean;
  selectedSiblings: string[];
  hasSelectedDescendant: boolean;
  isActive: boolean;
};

type Clauses = {
  collapsedByLevel: Map<number, string[]>;
  terminalByLevel: Map<number, string[]>;
};

const NOT_APPLICABLE = 'N/A';

const DEFAULT_DIMENSIONS = [
  'classification.global_therapeutic_area',
  'classification.global_level_1_indication_name',
  'classification.global_level_2_indication_name',
  'classification.global_level_3_indication_name',
  'classification.global_level_4_indication_name',
];

const createOptionNode = (name: string, level: number, parent?: string, isRoot = false): OptionNode => ({
  name,
  level,
  parent,
  children: [],
  selected: false,
  childrenCount: 0,
  selectedChildrenCount: 0,
  hasSelectedSiblings: false,
  selectedSiblings: [],
  isRoot,
  parentSelected: false,
  isOnlyChild: false,
  allDescendantsSelected: false,
  hasSelectedDescendant: false,
  isActive: false,
});

const buildSelectionLookup = (options: string[], selected: string[]): Record<string, OptionNode> => {
  const lookup: Record<string, OptionNode> = {};
  const selectedSet = new Set(selected);

  options.forEach(opt => {
    const segments = opt.split('__');
    const level = segments.length - 1;
    const parent = level > 0 ? segments.slice(0, -1).join('__') : undefined;
    const isRoot = level === 0;

    if (!lookup[opt]) {
      lookup[opt] = createOptionNode(opt, level, parent, isRoot);
    }

    if (parent && !lookup[parent]) {
      const grandParent = level > 1 ? segments.slice(0, -2).join('__') : undefined;
      lookup[parent] = createOptionNode(parent, level - 1, grandParent, level - 1 === 0);
    }

    if (parent) lookup[parent].children.push(opt);
  });

  selectedSet.forEach(sel => {
    if (lookup[sel]) lookup[sel].selected = true;
  });

  Object.values(lookup).forEach(node => {
    node.childrenCount = node.children.length;
    node.selectedChildrenCount = node.children.filter(child => lookup[child]?.selected).length;
  });

  Object.values(lookup).forEach(node => {
    if (!node.parent) return;
    const parentNode = lookup[node.parent];
    node.parentSelected = parentNode.selected;
    const siblings = parentNode.children.filter(child => child !== node.name);
    node.isOnlyChild = siblings.length === 0;
    node.selectedSiblings = siblings.filter(sibling => lookup[sibling]?.selected);
    node.hasSelectedSiblings = node.selectedSiblings.length > 0;
  });

  const nodesByDescendingLevel = Object.values(lookup).sort((a, b) => b.level - a.level);

  nodesByDescendingLevel.forEach(node => {
    node.hasSelectedDescendant = node.children.some(
      child => lookup[child]?.selected || lookup[child]?.hasSelectedDescendant,
    );

    if (node.children.length === 0) {
      node.allDescendantsSelected = node.selected;
    } else {
      node.allDescendantsSelected = node.children.every(child => lookup[child]?.allDescendantsSelected);
    }

    node.isActive = node.selected || node.hasSelectedDescendant;
  });

  return lookup;
};

const getValue = (node: OptionNode): string => node.name.split('__').pop()!;

const equalsFilter = (level: number, values: string[], dimensions: string[]): BinaryFilter => ({
  member: dimensions[level],
  operator: 'equals',
  values: Array.from(new Set(values)),
});

const terminalFilter = (level: number, values: string[], dimensions: string[]): Filter => {
  const nodeFilter = equalsFilter(level, values, dimensions);
  if (level + 1 >= dimensions.length) return nodeFilter;

  return { and: [nodeFilter, equalsFilter(level + 1, [NOT_APPLICABLE], dimensions)] };
};

const addValue = (target: Map<number, string[]>, level: number, value: string): void => {
  const values = target.get(level);
  if (values) values.push(value);
  else target.set(level, [value]);
};

const collectClauses = (node: OptionNode, lookup: Record<string, OptionNode>, clauses: Clauses): void => {
  if (node.selected && node.childrenCount > 0 && node.allDescendantsSelected) {
    addValue(clauses.collapsedByLevel, node.level, getValue(node));
    return;
  }

  if (node.selected) {
    addValue(clauses.terminalByLevel, node.level, getValue(node));
  }

  for (const childName of node.children) {
    const child = lookup[childName];
    if (child?.isActive) collectClauses(child, lookup, clauses);
  }
};

export const parseTaIndicationFilter = (
  selected: string[],
  options: string[],
  dimensions: string[] = DEFAULT_DIMENSIONS,
): Filter | undefined => {
  if (!selected.length) return undefined;

  const lookup = buildSelectionLookup(options, selected);
  const clauses: Clauses = { collapsedByLevel: new Map(), terminalByLevel: new Map() };

  Object.values(lookup)
    .filter(node => node.isRoot && node.isActive)
    .forEach(root => collectClauses(root, lookup, clauses));

  const filters: Filter[] = [];
  for (let level = 0; level < dimensions.length; level += 1) {
    const collapsed = clauses.collapsedByLevel.get(level);
    const terminal = clauses.terminalByLevel.get(level);

    if (collapsed) filters.push(equalsFilter(level, collapsed, dimensions));
    if (terminal) filters.push(terminalFilter(level, terminal, dimensions));
  }

  if (!filters.length) return undefined;

  return filters.length === 1 ? filters[0] : { or: filters };
};
